import React from "react";
import { BookingStep, TimeSlot, BookingLanguage } from "@/types/booking";
import type { SlotSelectionUpdateOptions } from "@/hooks/booking/useTimeSlots";
import StepRenderer from "./main-content/StepRenderer";
import { useMainStepData } from "./main-content/useMainStepData";
import { FormValues } from "@/components/booking-form/types";

// Define the component map type for consistency
type ComponentMap =
  | Record<string, string[]>
  | { internalUpdate: boolean }
  | { skipAddons: boolean };

interface MainStepContentProps {
  currentStep: BookingStep;
  handleProductSelect: (
    productIds: string[],
    componentMap?: ComponentMap,
  ) => void;
  handleClinicSelect: () => void;
  selectedProducts: string[];
  selectedClinic: string;
  handleClinicChange: (clinicId: string) => void;
  handleProductRemove: (productId: string) => void;
  handleBack: () => void;
  canGoBack: boolean;
  language: BookingLanguage;
  currentDate?: Date;
  handleDateSelect: (date: Date | undefined) => void;
  selectedTimeSlots: TimeSlot[];
  bookingType: "request";
  setBookingType: (type: "request") => void;
  handleTimeSelect: (
    times: string[],
    options?: SlotSelectionUpdateOptions,
  ) => void;
  handleDeleteTimeSlot: (slot: TimeSlot) => void;
  setCurrentStep: (step: BookingStep) => void;
  handleFormSubmit: (data: FormValues) => void;
  formData: Partial<FormValues>;
  bookingId: string;
  setBookingId: (id: string) => void;
  selectedAddOns: string[];
  onAddOnToggle: (productId: string) => void;
  viewMode: "packages" | "configure";
  setViewMode: (mode: "packages" | "configure") => void;
  selectedPackageType?: string; // Filter add-ons by package type
}

const MainStepContent = ({
  currentStep,
  handleProductSelect,
  handleClinicSelect,
  selectedProducts,
  selectedClinic,
  handleClinicChange,
  handleProductRemove,
  handleBack,
  canGoBack,
  language,
  currentDate,
  handleDateSelect,
  selectedTimeSlots,
  bookingType,
  setBookingType,
  handleTimeSelect,
  handleDeleteTimeSlot,
  setCurrentStep,
  handleFormSubmit,
  formData: formDataProp,
  bookingId,
  setBookingId,
  selectedAddOns = [],
  onAddOnToggle,
  viewMode,
  setViewMode,
  selectedPackageType,
}: MainStepContentProps) => {
  const {
    selectedProductDetails,
    allSelectedProductDetails,
    discount,
    formData,
    priceSummaryTranslations,
    packageComponentMap,
    allProducts,
  } = useMainStepData({
    selectedProducts,
    selectedAddOns,
    language,
    formData: formDataProp,
  });

  return (
    <StepRenderer
      currentStep={currentStep}
      handleProductSelect={handleProductSelect}
      handleClinicSelect={handleClinicSelect}
      selectedProducts={selectedProducts}
      selectedClinic={selectedClinic}
      handleClinicChange={handleClinicChange}
      handleProductRemove={handleProductRemove}
      handleBack={handleBack}
      canGoBack={canGoBack}
      language={language}
      currentDate={currentDate}
      handleDateSelect={handleDateSelect}
      selectedTimeSlots={selectedTimeSlots}
      bookingType={bookingType}
      setBookingType={setBookingType}
      handleTimeSelect={handleTimeSelect}
      handleDeleteTimeSlot={handleDeleteTimeSlot}
      setCurrentStep={setCurrentStep}
      handleFormSubmit={handleFormSubmit}
      formData={formData}
      discount={discount}
      bookingId={bookingId}
      setBookingId={setBookingId}
      selectedProductDetails={allSelectedProductDetails}
      priceSummaryTranslations={priceSummaryTranslations}
      packageComponentMap={packageComponentMap}
      selectedAddOns={selectedAddOns}
      onAddOnToggle={onAddOnToggle}
      allProducts={allProducts}
      viewMode={viewMode}
      setViewMode={setViewMode}
      selectedPackageType={selectedPackageType}
    />
  );
};

export default MainStepContent;
